import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Favorite,
  Delete,
  FilterList,
  Sort,
  ShoppingCart,
  Clear,
  FavoriteBorder,
} from "@mui/icons-material";
import ProductCard from "../../components/common/ProductCard";
import { useNavigate } from "react-router-dom";
import { useSEO } from "../../hooks/useSEO";
import { useApp } from "../../hooks/useApp";
import { useAuth, useFavorites } from "../../context/context";
import useResponsive from "../../hooks/useResponsive";

const Favorites = () => {
  const navigate = useNavigate();
  const {
    getUserFavorites,
    clearCache,
    getFavoritesCount,
    loading: favoritesLoading,
    isInitialized: favoritesInitialized,
    getSyncStatus, // 🔧 Nuevo estado de sincronización
    forceSyncPending, // 🔧 Función para forzar sincronización
  } = useFavorites(); // 🔧 Cambiar al nuevo hook
  const { user, isInitializing } = useAuth();
  const { openLoginModal } = useApp();
  const { isMobile } = useResponsive();

  const favorites = getUserFavorites();
  const syncStatus = getSyncStatus(); // 🔧 Obtener estado de sincronización

  // 🔧 MOVER TODOS LOS useState AL INICIO
  const [openClearDialog, setOpenClearDialog] = useState(false);
  const [sortBy, setSortBy] = useState("dateAdded");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // SEO para página de favoritos
  useSEO({
    title: "Mis Favoritos",
    description: `Mi lista de productos favoritos en Venta Usados Tattersall. ${getFavoritesCount()} productos guardados para consultar más tarde.`,
    keywords:
      "favoritos, productos guardados, lista deseos, venta usados tattersall",
    image: "/og-favorites.jpg",
    type: "website",
  });

  // Filtrar y ordenar favoritos
  const filteredAndSortedFavorites = React.useMemo(() => {
    let filtered = favorites;

    // Debug: Log para ver qué datos están llegando
    if (filtered.length > 0) {
      console.log("🔍 Datos de favorito para ProductCard:", filtered[0]);
    }

    // Filtrar por categoría
    if (filterCategory !== "all") {
      filtered = filtered.filter((fav) => fav.category === filterCategory);
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (fav) =>
          (fav.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (fav.model || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "price":
          // Arreglar: price ya es un número
          aValue =
            typeof a.price === "number"
              ? a.price
              : parseInt((a.price || "0").toString().replace(/[^\d]/g, ""));
          bValue =
            typeof b.price === "number"
              ? b.price
              : parseInt((b.price || "0").toString().replace(/[^\d]/g, ""));
          break;
        case "year":
          aValue = a.year || 0;
          bValue = b.year || 0;
          break;
        case "dateAdded":
        default:
          aValue = new Date(a.dateAdded || 0);
          bValue = new Date(b.dateAdded || 0);
          break;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [favorites, filterCategory, searchTerm, sortBy, sortOrder]);

  // 🔥 ARREGLAR: Solo verificar usuario cuando YA NO esté inicializando
  useEffect(() => {
    if (!isInitializing && !user) {
      console.log("🔐 Usuario no autenticado, abriendo modal de login");
      openLoginModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isInitializing]); // 🔥 REMOVIDO openLoginModal para evitar infinite loop

  // 🔥 MOSTRAR LOADING mientras inicializa la autenticación
  if (isInitializing) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h5" color="text.secondary">
            Verificando sesión...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Si no hay usuario después de la inicialización, mostrar mensaje
  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h4" gutterBottom color="text.secondary">
            Inicia sesión para ver tus favoritos
          </Typography>
        </Box>
      </Container>
    );
  }

  // 🔥 MOSTRAR LOADING si está cargando OR si no está inicializado aún
  if (favoritesLoading || !favoritesInitialized) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* 🌟 BANNER DE SINCRONIZACIÓN */}
        {/* <FavoritesSyncBanner /> */}

        {/* Header skeleton */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width={200} height={40} />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Skeleton variant="text" width={150} height={30} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Skeleton variant="rounded" width={100} height={36} />
              <Skeleton variant="rounded" width={120} height={36} />
            </Box>
          </Box>
        </Box>

        {/* Filtros skeleton */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width={200} height={30} />
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid key={item} size={{ xs: 12, sm: 6, md: 3 }}>
                <Skeleton variant="rounded" width="100%" height={40} />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Loading de cards skeleton */}
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
            <Grid key={item} size={{ xs: 12, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                {/* Header del producto */}
                <Box
                  sx={{
                    p: 2,
                    background:
                      "linear-gradient(135deg, #004b93 0%, #003366 100%)",
                  }}
                >
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={24}
                    sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                  />
                </Box>
                {/* Imagen */}
                <Skeleton variant="rectangular" width="100%" height={200} />
                {/* Contenido */}
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="70%" height={20} />
                  </Box>
                  <Box sx={{ textAlign: "center", mb: 2 }}>
                    <Skeleton
                      variant="text"
                      width="50%"
                      height={32}
                      sx={{ mx: "auto" }}
                    />
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={20}
                      sx={{ mx: "auto" }}
                    />
                  </Box>
                  <Skeleton variant="rounded" width="100%" height={48} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Loading indicator en el centro */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Cargando tus favoritos...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  // Obtener categorías únicas
  const categories = [...new Set(favorites.map((fav) => fav.category))];

  const handleClearAll = () => {
    clearCache();
    setOpenClearDialog(false);
  };

  // 🔧 Función para manejar sincronización manual
  const handleForceSync = async () => {
    try {
      await forceSyncPending();
    } catch (error) {
      console.error("Error al forzar sincronización:", error);
    }
  };

  // 🔥 SOLO MOSTRAR "No favoritos" cuando esté inicializado Y NO esté cargando Y realmente no hay favoritos
  if (favoritesInitialized && !favoritesLoading && favorites.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* 🌟 BANNER DE SINCRONIZACIÓN */}
        {/* <FavoritesSyncBanner /> */}

        <Box sx={{ textAlign: "center" }}>
          <FavoriteBorder sx={{ fontSize: 120, color: "grey.300", mb: 2 }} />
          <Typography variant="h4" gutterBottom color="text.secondary">
            No tienes productos favoritos
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Explora nuestro catálogo y agrega productos a tus favoritos haciendo
            click en el corazón
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/catalogo")}
            sx={{ px: 4 }}
          >
            Explorar Catálogo
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* 🌟 BANNER DE SINCRONIZACIÓN */}
      {/* <FavoritesSyncBanner /> */}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Favorite sx={{ color: "#ff4569", mr: 1, fontSize: 32 }} />
          <Typography variant="h3" component="h1" fontWeight="bold">
            Mis Favoritos
          </Typography>
          {/* 🔧 INDICADOR DE SINCRONIZACIÓN en el header */}
          <Box sx={{ ml: 2 }}>
            {/* <FavoritesSyncIndicator showDetails={true} /> */}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {getFavoritesCount()} producto{getFavoritesCount() !== 1 ? "s" : ""}{" "}
            guardado{getFavoritesCount() !== 1 ? "s" : ""}
            {/* 🔧 Mostrar estado de sincronización */}
            {syncStatus.pending > 0 && (
              <Chip
                label={`${syncStatus.pending} pendiente${
                  syncStatus.pending > 1 ? "s" : ""
                }`}
                color="warning"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {/* 🔧 Botón de sincronización manual */}
            {syncStatus.pending > 0 && syncStatus.isOnline && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleForceSync}
                disabled={favoritesLoading}
              >
                Sincronizar Pendientes
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Delete />}
              onClick={() => setOpenClearDialog(true)}
              color="error"
              size="small"
            >
              Limpiar Todo
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 🔧 Alerta de estado offline */}
      {!syncStatus.isOnline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            📱 <strong>Sin conexión</strong> - Los cambios en favoritos se
            guardan localmente y se sincronizarán automáticamente cuando vuelva
            la conexión.
          </Typography>
        </Alert>
      )}

      {/* Filtros y ordenamiento */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">Filtros y Ordenamiento</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Buscar en favoritos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              placeholder="Nombre, modelo..."
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={filterCategory}
                label="Categoría"
                onChange={(e) => setFilterCategory(e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="all">Todas las categorías</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={sortBy}
                label="Ordenar por"
                onChange={(e) => setSortBy(e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="dateAdded">Fecha agregado</MenuItem>
                <MenuItem value="name">Nombre</MenuItem>
                <MenuItem value="price">Precio</MenuItem>
                <MenuItem value="year">Año</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Orden</InputLabel>
              <Select
                value={sortOrder}
                label="Orden"
                onChange={(e) => setSortOrder(e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="desc">Descendente</MenuItem>
                <MenuItem value="asc">Ascendente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Resultados de filtros */}
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredAndSortedFavorites.length} de{" "}
            {getFavoritesCount()} favoritos
          </Typography>

          {(filterCategory !== "all" || searchTerm) && (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={() => {
                setFilterCategory("all");
                setSearchTerm("");
              }}
            >
              Limpiar filtros
            </Button>
          )}
        </Box>
      </Paper>

      {/* Lista de favoritos */}
      {filteredAndSortedFavorites.length === 0 ? (
        <Alert severity="info">
          No se encontraron productos que coincidan con los filtros
          seleccionados.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredAndSortedFavorites.map((favorite) => (
            <Grid key={favorite.id} size={{ xs: 6, md: 3 }}>
              <ProductCard
                mode={isMobile ? "minimal" : "normal"}
                product={favorite}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Acciones adicionales */}
      <Box sx={{ mt: 6, textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          ¿No encontraste lo que buscabas?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Explora nuestro catálogo completo para descubrir más productos y
          ofertas exclusivas.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Recuerda que puedes agregar productos a tus favoritos para
          consultarlos más tarde.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          ¡Feliz exploración!
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/catalogo")}
          >
            Seguir Explorando
          </Button>
        </Box>
      </Box>

      {/* Dialog de confirmación para limpiar favoritos */}
      <Dialog open={openClearDialog} onClose={() => setOpenClearDialog(false)}>
        <DialogTitle>¿Limpiar todos los favoritos?</DialogTitle>
        <DialogContent>
          <Typography>
            Esta acción eliminará todos los {getFavoritesCount()} productos de
            tu lista de favoritos
            {syncStatus.pending > 0 &&
              `, incluyendo ${syncStatus.pending} favorito${
                syncStatus.pending > 1 ? "s" : ""
              } pendiente${syncStatus.pending > 1 ? "s" : ""} de sincronizar`}
            . No podrás deshacer esta acción.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClearDialog(false)}>Cancelar</Button>
          <Button onClick={handleClearAll} color="error" variant="contained">
            Limpiar Todo
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Favorites;
